import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import CommonQuestions from '@/components/marketing/CommonQuestions';
import CapabilitySpine from '@/components/marketing/CapabilitySpine';
import MarginCalculator from '@/components/marketing/MarginCalculator';
import FunderDiscovery from '@/components/marketing/FunderDiscovery';
import { CBO_COUNT, LANGUAGES, contactHref } from '@/components/marketing/siteFacts';
import useFunderCoverage, { plansWithOwnRules } from '@/hooks/useFunderCoverage';
import { ArrowRight, CheckCircle, Network, Globe2, Inbox, Banknote, FileCheck2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * For community organizations & hubs — the people who deliver the care.
 *
 * This page used to list the same modules three times (six pillars, an
 * eighteen-module grid and a fourteen-row comparison). It now describes the
 * product with the six capabilities every page shares, and carries the
 * "join the network" material that used to sit, mislabelled, on the hospital
 * page: an organization receives referrals here and gets paid by each funder.
 */
export default function HubsSolutionsPage() {
  usePageMeta(SEO_ROUTES['/solutions/hubs']);

  const [providerCount, setProviderCount] = useState('1.7M+');
  // Live from the platform (falls back to the last measured figures), not typed in.
  const { coverage } = useFunderCoverage();
  const CA_PLANS_MAPPED = coverage.mapped_plans.count;
  const CA_PLANS_PLAN_SPECIFIC = plansWithOwnRules(coverage.mapped_plans);

  useEffect(() => {
    fetch('/provider-api/stats')
      .then(res => res.json())
      .then(data => {
        if (data.total_providers) {
          setProviderCount(data.total_providers >= 1000000 ? (data.total_providers / 1000000).toFixed(1) + 'M+' : Math.floor(data.total_providers / 1000).toLocaleString() + 'K+');
        }
      })
      .catch(() => {
        fetch(`${API_BASE}/api/public/network-stats`)
          .then(res => res.json())
          .then(data => {
            if (data.totalProviders) {
              setProviderCount(data.totalProviders >= 1000000 ? (data.totalProviders / 1000000).toFixed(1) + 'M+' : Math.floor(data.totalProviders / 1000).toLocaleString() + 'K+');
            }
          })
          .catch(() => {});
      });
  }, []);

  const joinSteps = [
    {
      icon: Inbox,
      title: 'Receive referrals in one inbox',
      description: 'Referrals from plans, hospitals and other organizations arrive in one place, matched to the services you offer, the area you cover and the languages you speak.',
    },
    {
      icon: FileCheck2,
      title: 'Record the visit once',
      description: 'Your worker documents the service on a phone, in the field. That one record is what every funder’s submission is built from.',
    },
    {
      icon: Banknote,
      title: 'Get paid by each funder',
      description: 'The same encounter is billed to every plan, county contract or grant it qualifies for, each the way that funder takes it, with proof it was not billed twice.',
    },
  ];

  const comparison = [
    { category: 'Referral networks', stops: 'Close the referral and stop before the money moves.' },
    { category: 'RCM vendors & clearinghouses', stops: 'Need an 837. Many plans here take a workbook, a portal upload or an invoice.' },
    { category: 'Fund accounting', stops: 'Handles the grant and never touches a claim.' },
    { category: 'WeaveIn', stops: 'The organization’s own system of record across every plan, grant and program it bills — front office to back office.' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full">
                <Network className="w-5 h-5 text-v2accent mr-2" />
                <span className="text-ink font-medium">For Community Organizations &amp; Hubs</span>
              </div>
              <div className="inline-flex items-center px-3 py-1.5 bg-v2accent-soft border border-v2accent rounded-full">
                <span className="text-v2accent text-sm font-semibold">CalAIM Ready</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              You delivered the care.<br />
              <span className="text-v2accent">You should be paid for it.</span>
            </h1>
            <p className="text-xl text-ink-500 mb-4">
              Every plan wants the same work a different way — its own service menu, its
              own rules, its own paperwork. Your staff absorb that difference, and it comes
              out of the time they have for members.
            </p>
            <p className="text-base text-ink-400 mb-8">
              WeaveIn holds the difference instead. A community health worker captures the
              visit once, where it happens, in the member's language. The system applies
              the right plan's rules to it and keeps the record that proves your
              organization delivered the service.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={contactHref('deliver')}
                className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
              >
                Talk to us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to={contactHref('guide', 'Hub guide')}
                className="inline-flex items-center px-8 py-4 bg-v2accent-soft text-ink text-lg font-semibold rounded-lg hover:bg-ink/20 transition-colors"
              >
                Request the hub guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Funder coverage */}
      <section className="py-12 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
            <Globe2 className="w-10 h-10 text-v2accent" aria-hidden="true" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                Your funders, whichever state they are in
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Each plan’s service menu, rates, modifiers and filing window are held as data, with effective dates.
                {' '}{CA_PLANS_MAPPED} California Medi-Cal plans have their rules held as data today, {CA_PLANS_PLAN_SPECIFIC} of them with plan-specific rules. Any U.S. payer is reachable for
                eligibility, claims and remittance through national clearinghouse connections, and adding a plan’s rules is a
                configuration change, not a release.
              </p>
            </div>
            <Link to="/funder-coverage" className="inline-flex items-center font-semibold text-ink hover:underline whitespace-nowrap">
              See funder coverage
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      <CapabilitySpine linkToPlatform />

      {/* Join the network: receive referrals, get paid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Receive referrals. Deliver. Get paid by each funder.
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              If your organization addresses housing, food, transportation or any other health-related social need, there is a
              place for you in the network — and a way to be paid for the work you already do.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {joinSteps.map((s) => (
              <div key={s.title} className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <s.icon className="w-8 h-8 text-v2accent mb-4" aria-hidden="true" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarginCalculator />

      <FunderDiscovery tone="light" />

      {/* CalAIM & Medicaid Readiness */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Built for CalAIM, configured for any state
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              California is where the rules are deepest today. The same rule rows, fee schedules and member-file layouts carry
              another state’s Medicaid program, county contracts and grants.
            </p>
            <ul className="space-y-3">
              {[
                'CalAIM Community Supports — CS-1 through CS-14 service mapping, per plan',
                'Enhanced Care Management workflow and member information files',
                'State fee schedules and plan rate cards with effective dates',
                'Service authorization tracking, including EDI 278',
                'HEDIS quality gap tracking for plan contracts',
              ].map((item) => (
                <li key={item} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/platform#modules" className="mt-6 inline-flex items-center text-sm font-semibold text-ink hover:underline">
              Every module, by capability
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Network stats */}
      <section className="py-14 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{CBO_COUNT}</div>
              <div className="text-gray-600 dark:text-gray-400">Community organizations on the platform</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{providerCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Providers in the directory</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{LANGUAGES}</div>
              <div className="text-gray-600 dark:text-gray-400">Languages for member outreach</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-v2accent mb-2">{CA_PLANS_MAPPED}</div>
              <div className="text-gray-600 dark:text-gray-400">Medi-Cal plans with rules held as data</div>
            </div>
          </div>
        </div>
      </section>

      {/* Where each category stops */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-ink dark:text-white mb-4 text-center">
            Where each category stops
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 text-center max-w-2xl mx-auto mb-10">
            Others bill inside the one program they administer. Nobody else runs the organization’s whole book.
          </p>
          <div className="divide-y divide-gray-100 dark:divide-gray-800 border-y border-gray-100 dark:border-gray-800">
            {comparison.map((row) => (
              <div key={row.category} className="grid md:grid-cols-[260px_1fr] gap-2 md:gap-6 py-4">
                <div className={`font-semibold ${row.category === 'WeaveIn' ? 'text-ink' : 'text-gray-800 dark:text-gray-100'}`}>
                  {row.category}
                </div>
                <div className="text-gray-600 dark:text-gray-300">{row.stops}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Bring your funders. We will map them.
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            Tell us which plans, counties and grants you bill. We will show you which are already mapped, and what it takes
            to add the rest.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to={contactHref('deliver')}
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors shadow-lg"
            >
              Talk to us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to={contactHref('guide', 'Hub guide')}
              className="inline-flex items-center px-8 py-4 bg-transparent text-ink text-lg font-semibold rounded-lg border-2 border-ink hover:bg-ink hover:text-paper transition-colors"
            >
              Request the hub guide
            </Link>
          </div>
        </div>
      </section>

      <CommonQuestions
        questions={[
          {
            q: 'How is this different from an RCM or billing vendor?',
            a: 'Most billing vendors assume an electronic claim and a clearinghouse. A lot of this work is not billed that way — plans take a workbook, a portal upload or an invoice. WeaveIn starts from the encounter and the plan’s own rules rather than from a claim format.',
          },
          {
            q: 'Do you support the rules for our specific plans?',
            a: `Each plan’s service menu, rates and modifiers are held as data rather than code, with effective dates. ${CA_PLANS_MAPPED} California Medi-Cal plans (${CA_PLANS_PLAN_SPECIFIC} with plan-specific rules) are mapped today; any U.S. payer is reachable through national clearinghouse connections, and adding a plan’s rules is a configuration change, not a release. An agent drafts the rule rows from the plan’s published manual and a person approves each one.`,
          },
        ]}
      />

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
